import './map_style_presets_display.less';

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';

import map from 'lodash/map';
import each from 'lodash/each';
import includes from 'lodash/includes';
import values from 'lodash/values';
import keys from 'lodash/keys';

import StylePresetHintBox from 'zoomforth_component_lib/style_preset_hint_box.jsx';
import TruncatedText from 'zoomforth_component_lib/truncated_text.jsx';

import {
  ArrowRightIcon,
  BanIcon,
  DragIcon
} from 'zoomforth_component_lib/app_modern_icons.jsx';

import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove
} from 'zoomforth_component_lib/react_sortable_hoc.jsx';

// Must match a CSS className
const SORTABLE_HELPER_CLASSNAME = 'sortable-helper-style-presets-dnj49h2s';

const DragHandle = SortableHandle(()=> (
  <DragIcon
    className="field-drag-handle"
    style={{ marginRight:'5px' }}
  />
));

const SortableStylePreset = SortableElement((props)=> <div {...props} />);
const SortableStylePresetsContainer = SortableContainer((props)=> <div {...props} />);

class MapStylePresetsDisplay extends Component {

  constructor(props) {
    super(props);

    this.state = {
      oldThemeStylePresets: props.oldThemeStylePresets,
      newThemeStylePresets: props.newThemeStylePresets,
      mappedStylePresets: this.makeMappedStylePresets(
        props.oldThemeStylePresets,
        props.newThemeStylePresets
      )
    };

    autobind(this);
  }

  componentDidMount() {
    this.props.onStylePresetMappingChange(this.state.mappedStylePresets);
  }

  makeMappedStylePresets(oldStylePresets, newStylePresets) {
    const mappedStylePresets = {};

    each(oldStylePresets, (preset, idx)=> {
      if (!preset.isDummy && !newStylePresets[idx].isDummy) {
        mappedStylePresets[preset.className] = newStylePresets[idx].className;
      }
    });

    return mappedStylePresets;
  }

  handleStylePresetsReorder({ oldIndex, newIndex }) {
    const newThemeStylePresets = arrayMove(
      this.state.newThemeStylePresets,
      oldIndex,
      newIndex
    );

    const mappedStylePresets = this.makeMappedStylePresets(
      this.state.oldThemeStylePresets,
      newThemeStylePresets
    );

    this.setState({
      mappedStylePresets,
      newThemeStylePresets
    });

    this.props.onStylePresetMappingChange(mappedStylePresets);
  }

  getShouldShowElementAsDimmed(preset) {
    return (
      !includes(keys(this.state.mappedStylePresets), preset.className) &&
      !includes(values(this.state.mappedStylePresets), preset.className)
    );
  }

  render() {
    const {
      oldThemeName,
      newThemeName
    } = this.props;

    const {
      oldThemeStylePresets,
      newThemeStylePresets
    } = this.state;

    return (
      <div className="map-style-preset-pane-wrapper">
        <div className="left-pane">
          <div className="theme-header">
            <h4>Old Theme</h4>
            <h2>
              <TruncatedText>
                { oldThemeName }
              </TruncatedText>
            </h2>
          </div>
          <div className="ordered-style-presets">
            {
              map(oldThemeStylePresets, (stylePreset, idx)=>
                <div
                  key={`${stylePreset.className}${idx}`}
                  className={classNames('style-preset-wrapper', {
                    'display-as-dimmed': this.getShouldShowElementAsDimmed(stylePreset)
                  })}
                >
                  <TruncatedText>
                    {
                      stylePreset.isDummy ?
                        ''
                      :
                        <Fragment>
                          <StylePresetHintBox 
                            style={{marginRight:'5px'}}
                            {...stylePreset.variables}
                          /> {stylePreset.displayName}
                        </Fragment>
                    }
                  </TruncatedText>
                </div>
              )
            }
          </div>
        </div>

        <div className="center-pane">
          {
            map(oldThemeStylePresets, (stylePreset, idx)=> (
              <div
                key={`${stylePreset.className}${idx}`}
                className={classNames('style-preset-mapping-icon', {
                  'display-as-dimmed': this.getShouldShowElementAsDimmed(stylePreset)
                })}
              >
                {
                  this.getShouldShowElementAsDimmed(stylePreset) ?
                    <BanIcon />
                  :
                    <ArrowRightIcon />
                }
              </div>
            ))
          }
        </div>

        <div className="right-pane">
          <div className="theme-header">
            <h4>New Theme</h4>
            <h2>
              <TruncatedText>
                { newThemeName }
              </TruncatedText>
            </h2>
          </div>
          <div className="ordered-style-presets">
            <SortableStylePresetsContainer
              lockAxis="y"
              distance={5}
              helperClass={SORTABLE_HELPER_CLASSNAME}
              onSortEnd={this.handleStylePresetsReorder}
            >
              {
                map(newThemeStylePresets, (stylePreset, idx)=>
                  <SortableStylePreset
                    index={idx}
                    key={`${stylePreset.className}${idx}`}
                    className={classNames('style-preset-wrapper', {
                      'display-as-dimmed': this.getShouldShowElementAsDimmed(stylePreset)
                    })}
                  >
                    <DragHandle />
                    <TruncatedText>
                      {
                        stylePreset.isDummy ?
                          ''
                        :
                          <Fragment>
                            <StylePresetHintBox 
                              style={{marginRight:'5px'}}
                              {...stylePreset.variables}
                            /> {stylePreset.displayName}
                          </Fragment>
                      }
                    </TruncatedText>
                  </SortableStylePreset>
                )
              }
            </SortableStylePresetsContainer>
          </div>
        </div>
      </div>
    );
  }

}

MapStylePresetsDisplay.propTypes = {
  newThemeName: PropTypes.string.isRequired,
  oldThemeName: PropTypes.string.isRequired,
  newThemeStylePresets: PropTypes.array.isRequired,
  oldThemeStylePresets: PropTypes.array.isRequired
};

export default MapStylePresetsDisplay;
